import {
  Stack,
  Text,
  Button,
  Group,
  Alert,
  Autocomplete,
  TextInput,
} from '@mantine/core';
import { ResponsiveDrawer, useIsBottomSheet } from '../UI/ResponsiveDrawer';
import { ResponsiveSelect } from '../UI/ResponsiveSelect';
import { notifications } from '@mantine/notifications';
import { DrawerHeader } from '../UI/DrawerHeader';
import {
  IconAlertCircle,
  IconShoppingCart,
  IconCarrot,
  IconTagStarred,
  IconWorldStar,
} from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { searchIngredients } from '../../data/ingredientDatabase';
import { ProductPreferenceScope } from './ProductPreferences';

type ProductPreferenceCategory = 
  | "produce" 
  | "meat" 
  | "dairy" 
  | "pantry_dry_goods" 
  | "spices_seasonings" 
  | "frozen" 
  | "bakery" 
  | "beverages" 
  | "other";

type UserProductPreference = {
  id: string;
  scope: ProductPreferenceScope;
  itemName?: string;
  category?: ProductPreferenceCategory;
  customPreference?: string;
  createdAt: string;
  lastUpdated: string;
};

interface ProductPreferenceDrawerProps {
  opened: boolean;
  onClose: () => void;
  preference?: UserProductPreference;
  onSave: (preference: UserProductPreference) => void;
}

export const ProductPreferenceDrawer = ({ 
  opened, 
  onClose, 
  preference, 
  onSave 
}: ProductPreferenceDrawerProps) => {
  const isBottomSheet = useIsBottomSheet();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [scope, setScope] = useState<ProductPreferenceScope>('item');
  const [itemName, setItemName] = useState('');
  const [category, setCategory] = useState<string>('');
  const [customPreference, setCustomPreference] = useState('');

  const scopeOptions = [
    { value: 'item', label: 'Specific ingredient', icon: <IconCarrot size={14} /> },
    { value: 'category', label: 'Category', icon: <IconTagStarred size={14} /> },
    { value: 'global', label: 'Everything', icon: <IconWorldStar size={14} /> },
  ];

  const categories: { value: string; label: string }[] = [
    { value: 'produce', label: 'Produce' },
    { value: 'meat', label: 'Meat' },
    { value: 'dairy', label: 'Dairy' },
    { value: 'pantry_dry_goods', label: 'Pantry & dry goods' },
    { value: 'spices_seasonings', label: 'Spices & seasonings' },
    { value: 'frozen', label: 'Frozen' },
    { value: 'bakery', label: 'Bakery' },
    { value: 'beverages', label: 'Beverages' },
    { value: 'other', label: 'Other' },
  ];

  // Initialize form when modal opens or preference changes
  useEffect(() => {
    if (preference) {
      // Editing existing preference
      setScope(preference.scope);
      setItemName(preference.itemName || '');
      setCategory(preference.category || '');
      setCustomPreference(preference.customPreference || '');
    } else {
      // Adding new preference
      setScope('item');
      setItemName('');
      setCategory('');
      setCustomPreference('');
    }
    setError(null);
  }, [preference, opened]);

  const handleSave = async () => {
    if (!user) return;
    
    // Validation
    if (!customPreference.trim()) {
      setError('Please enter a preference');
      return;
    }
    
    if (scope === 'item' && !itemName.trim()) {
      setError('Please enter an ingredient name');
      return;
    }
    
    if (scope === 'category' && !category) {
      setError('Please select a category');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = await user.getIdToken();
      
      // Prepare request data based on scope
      let url = '';
      let requestData: any = {
        customPreference: customPreference.trim(),
        scope,
      };

      if (scope === 'item') {
        url = `${process.env.REACT_APP_API_URL}/api/product-preferences/${encodeURIComponent(itemName.trim())}`;
        if (category) {
          requestData.category = category;
        }
      } else if (scope === 'category') {
        url = `${process.env.REACT_APP_API_URL}/api/product-preferences/category-${category}`;
        requestData.category = category;
      } else {
        url = `${process.env.REACT_APP_API_URL}/api/product-preferences/global-preference`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error('Failed to save preference');
      }

      const result = await response.json();
      
      onSave(result.preference);
      onClose();
      
      notifications.show({
        title: 'Success',
        message: preference ? 'Preference updated' : 'Preference saved',
        color: 'green',
      });
    } catch (error: any) {
      console.error('Error saving preference:', error);
      setError(error.message || 'Failed to save preference');
    } finally {
      setSaving(false);
    }
  };

  const getScopeDescription = () => {
    switch (scope) {
      case 'item':
        return 'This preference will apply only to this specific ingredient';
      case 'category':
        return 'This preference will apply to all items in this category';
      case 'global':
        return 'This preference will apply to all grocery shopping';
      default:
        return '';
    }
  };

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      title={
        <DrawerHeader
          icon={<IconShoppingCart size={18} />}
          title={preference ? 'Edit shopping preference' : 'Add shopping preference'}
        />
      }
      size="md"
    >
      <Stack gap="md">
        {/* Error Message */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
            radius="lg"
          >
            {error}
          </Alert>
        )}

        {/* Scope Selection */}
        <ResponsiveSelect
          label="Apply to"
          placeholder="Choose what this preference applies to"
          value={scope}
          onChange={(value) => setScope(value as ProductPreferenceScope)}
          data={scopeOptions}
          required
        />

        <Text size="xs" c="dimmed" style={{ marginTop: '-8px' }}>
          {getScopeDescription()}
        </Text>

        {/* Item-specific fields */}
        {scope === 'item' && (
          <Autocomplete
            label="Ingredient name"
            placeholder="e.g. tomatoes, olive oil, chicken breast"
            value={itemName}
            onChange={setItemName}
            data={itemName.length >= 2 ? searchIngredients(itemName, 8).map(ing => ing.displayName ?? ing.name) : []}
            limit={8}
            required
            comboboxProps={{ withinPortal: !isBottomSheet }}
          />
        )}

        {/* Category field */}
        {scope === 'category' && (
          <ResponsiveSelect
            label="Category"
            placeholder="Select a category"
            value={category}
            onChange={(value) => setCategory(value || '')}
            data={categories}
            required
          />
        )}

        {/* Custom preference field */}
        <TextInput
          label="Shopping preference"
          placeholder="e.g. prefer low fat, Heinz brand, buy in bulk"
          value={customPreference}
          onChange={(e) => setCustomPreference(e.target.value)}
          maxLength={100}
          required
        />

        <Text size="xs" c="dimmed">
          Be specific about brands, quality, nutrition, size, or other preferences to help find the right products.
        </Text>

        {/* Actions */}
        <Group justify="flex-end" gap="sm" pt="md">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            loading={saving}
          >
            {preference ? 'Update' : 'Save'} preference
          </Button>
        </Group>
      </Stack>
    </ResponsiveDrawer>
  );
};
